/* SLIDE 9 */
/* SLIDE 10 */

import React, { useState, useEffect } from "react";
import ButtonBot from "shared/components/ButtonBot";
import areaSettingIcon from "assets/icons/areaSettingIcon.svg";
import editAreaIcon from "assets/icons/editAreaIcon.svg";
import deleteAreaIcon from "assets/icons/deleteAreaIcon.svg";
import closeIcon from 'assets/icons/close.svg';
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Popup from "shared/components/Popup";
import SubHeader from "shared/components/SubHeader";
import addButton from "assets/icons/add.svg";
import { defaultAreaTypes } from '../../../helpers/constants';
import { updateAreaType, getAreaTypeList, getParkingSpaceName, insertOrUpdateSpaceName, getAreaTypeDetail } from "../usecases/ParkingUsecases";
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';

const ParkingSpotSettings = () => {
  const [spotName, setSpotName] = useState("");
  const [spotType, setSpotType] = useState("");
  const [areaTypeName, setAreaTypeName] = useState("");
  const [areaTypeEdit, setAreaTypeEdit] = useState(null);
  const [status, setStatus] = useState("created");
  const [spotTypes, setSpotTypes] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupAreaSetting, setPopupAreaSetting] = useState(false);
  const [popupAreaDelete, setPopupAreaDelete] = useState(false);
  const [popupAreaTypeUsing, setPopupAreaTypeUsing] = useState(false);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    setLoading(true);
    const fetchSpotTypes = async () => {
      try {
        const response = await getParkingSpaceName(user.parkingId, location.state?.spaceNumber);
        const parkingSpotInfo = response?.[0];
        const selectedTypeId = parkingSpotInfo?.defaultType || parkingSpotInfo?.areaTypeId || "NORMAL";
    
        const apiAreaTypeList = await getAreaTypeList(user.parkingId);
        setSpotName(parkingSpotInfo?.areaName || "");
        setSpotTypes(mergeSpotTypes(apiAreaTypeList));
        setSpotType(selectedTypeId);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch spot types:", error);
        setLoading(false);
      }
    };
    fetchSpotTypes();
  }, [location.state?.spaceNumber]);

  const mergeSpotTypes = (apiList = []) => {
    const defaultList = Object.entries(defaultAreaTypes).map(([key, value]) => ({
      id: key,
      name: value,
      isDefault: true,
    }));

    const customList = apiList.map((item) => ({
      id: item.areaTypeId,
      name: item.areaTypeName,
      isDefault: false,
    }));
    return [...defaultList, ...customList];
  };

  const handleTypeSelect = (type) => {
    setSpotType(type.id);
  };

  const handleSave = async () => {
    try {
      const selectedType = spotTypes.find(type => type.id === spotType);
      const isDefault = selectedType?.isDefault;
      const data = {
        parkingId: user.parkingId,
        spaceNumber: location.state?.spaceNumber,
        areaName: spotName,
        areaTypeId: isDefault ? "" : selectedType.id,
        defaultType: isDefault ? selectedType.id : "",
        userId: user.userId,
      }
      await insertOrUpdateSpaceName(data);
      navigate("/parking_setting");
    } catch (error) {
      console.error("Failed to reload area types:", error);
    }
  };

  const onCancel = () => {
    navigate("/parking_setting");
  };

  const handleAddButton = async () => {
    setAreaTypeEdit(null)
    setAreaTypeName("");
    setShowPopup(true);
    setStatus("created");
  };

  const handleEditButton = async () => {
    const response = await getAreaTypeDetail(user.parkingId, areaTypeEdit.id);
    const areaType = response?.[0];
    setAreaTypeName(areaType.areaTypeName);
    setShowPopup(true);
    setStatus("updated");
  };

  const handleDeleteButton = async () => {
    setPopupAreaDelete(true);
    setStatus("deleted");
  };

  const handleCloseButton = () => {
    setStatus("");
    setAreaTypeName("");
    setShowPopup(false);
  };

  const handlePopupEditCloseButton = () => {
    setStatus("");
    setPopupAreaDelete(false);
  };

  const editAreaType = async () => {
    if (!areaTypeName.trim() && (status == 'update' || status == 'created')) {
      alert("전용구획명을 입력해주세요.");
      return;
    }
    try {
      let data = {};
      if (status == 'updated' || status == 'deleted') {
        data = {
          parkingId: user.parkingId,
          areaTypeId: areaTypeEdit.id,
          areaTypeName: areaTypeName,
          status: status,
          userId: user.userId,
        };
      } else if (status == 'created') {
        data = {
          parkingId: user.parkingId,
          areaTypeName: areaTypeName,
          status: status,
          userId: user.userId,
        };
      }

      const responseEdit = await updateAreaType(data);
      if (responseEdit.success == false) { // CASE DELETE AREA TYPE IS USING
        handlePopupEditCloseButton();
        setPopupAreaSetting(false);
        setPopupAreaTypeUsing(true);
        return false;
      }
      const apiAreaTypeList = await getAreaTypeList(user.parkingId);
      setSpotTypes(mergeSpotTypes(apiAreaTypeList));
      if (status == 'created') {
        setShowPopup(false);
      } else if (status == 'updated') {
        setShowPopup(false);
        setPopupAreaSetting(false);
      } else if (status == 'deleted') {
        if (spotType == areaTypeEdit.id) {
          setSpotType("NORMAL");
        }
        setPopupAreaDelete(false);
        setPopupAreaSetting(false);
      }

    } catch (error) {
      console.error("Failed to insert area type:", error);
    }
  };

  return (
    loading ? <ParkingSpotSettingSkeleton /> : <div className="parking-status-container">
      <div className="content">
        <SubHeader>
          <div className="nl-row f-column">
            <div className={`sub-header-text w-full`}>
              <span className="htColorGray2">선택 주차면 : </span>
              <span className="htBold">주차면 {String(location.state?.spaceNumber).padStart(2, '0')}</span>
            </div>
          </div>

        </SubHeader>
        <div className="input-section">
          <div className="input-row">
            <label>별칭 설정</label>
            <input
              type="text"
              placeholder="별칭 설정"
              value={spotName}
              onChange={(e) => {
                setSpotName(e.target.value);
              }}
              className="mn-input"
            />
          </div>
        </div>

        <div className="type-selection">
          <div className="select-title">전용구획 설정</div>
          <div className="spot-types has-fixed-buttons">
            {spotTypes.map((type) => (
              <div
                key={type.id}
                className={`type-button nh-relative ${spotType === type.id ? "active" : ""
                  }`}
                onClick={() => handleTypeSelect(type)}
              >
                <div className="korean-label nh-slot">{type.name}</div>
                {!type.isDefault && (
                  <div className="nh-area-setting" onClick={(e) => {
                    e.stopPropagation();
                    setAreaTypeEdit(type);
                    setPopupAreaSetting(true);
                  }}>
                    <img
                      src={areaSettingIcon}
                      alt="Custom"
                      className="custom-type-icon"
                    />
                  </div>

                )}
              </div>
            ))}
            <div className="add-button-select" style={{ border: '1px dashed #D2D2D2', padding: 16, backgroundColor: "#F8F8F8" }} onClick={handleAddButton}>
              <img src={addButton} alt="" />
            </div>
          </div>
        </div>
      </div>
      <ButtonBot
        textNo={"취소"}
        textYes={"저장"}
        handleNo={onCancel}
        handleYes={handleSave}
      />
      {popupAreaSetting && (
        <Popup
          setVisible={setPopupAreaSetting}
          classPopup="nh-popup mn-spot-setting-popup"
          onClose={() => { setAreaTypeEdit(null); setPopupAreaSetting(false) }}
        >
          <div className="input-section nh-relative">
            <div className="nh-popup-row">
              <div className="nl-row" onClick={handleEditButton}>
                <img
                  src={editAreaIcon}
                  alt="editAreaIcon"
                />
                <span >수정</span>
              </div>
            </div>
            <div className="nh-popup-row">
              <div className="nl-row" onClick={handleDeleteButton}>
                <img
                  src={deleteAreaIcon}
                  alt="deleteAreaIcon"
                />
                <span >삭제</span>
              </div>
            </div>
           
          </div>
        </Popup>
      )}
      {showPopup && (
        <Popup
          title="전용구획 추가"
          handleYes={editAreaType}
          handleNo={handleCloseButton}
          textYes="저장"
          textNo="닫기"
          setVisible={setShowPopup}
        >
         
          <div className="input-section">
            <div className="input-row">
              <label>전용구획명</label>
              <input
                type="text"
                placeholder="전용구획명"
                value={areaTypeName}
                onChange={(e) => setAreaTypeName(e.target.value)}
              />
            </div>
          </div>
        </Popup>
      )}
      {popupAreaDelete && (
        <Popup
          title="삭제"
          handleYes={editAreaType}
          handleNo={handlePopupEditCloseButton}
          textYes="확인"
          textNo="취소"
          setVisible={setPopupAreaDelete}
          classPopup="nh-popup-delete"
        >
          <div className="input-section">
            <label>정말로 삭제할까요?</label>
          </div>
        </Popup>
      )}
      {popupAreaTypeUsing && (
        <Popup
          title="삭제"
          handleYes={() => { setPopupAreaTypeUsing(false) }}
          textYes="저장"
          setVisible={setPopupAreaTypeUsing}
          classPopup="nh-popup-delete"
        >
          <div className="input-section">
            <label>사용 중인 전용구획은 삭제할 수 없습니다.</label>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default ParkingSpotSettings;

const ParkingSpotSettingSkeleton = () => {
  return (
    <SkeletonTheme baseColor="#e0e0e0" highlightColor="#f5f5f5">
      <div style={{ padding: '20px'}}>
        <Skeleton height={30} width={200} style={{ marginBottom: '20px' }} />

        <div style={{ marginBottom: '10px' }}>
          <Skeleton height={20} width={100} style={{ marginBottom: '5px' }} />
          <Skeleton height={40} width="100%" />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <Skeleton height={20} width={100} style={{ marginBottom: '5px' }} />
          <Skeleton height={40} width="100%" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '20px' }}>
          {Array(6)
            .fill()
            .map((_, index) => (
              <Skeleton key={index} height={50} />
            ))}
          {/* 2 nút phụ */}
          <Skeleton height={50} />
          <Skeleton height={50} />
          <div></div>
        </div>

        {/* <Skeleton circle={true} height={40} width={40} /> */}
      </div>
    </SkeletonTheme>
  );
};
